import {
    getCoreApiUrl,
    ILandingPageContact,
    IUpsertLandingPageContactDto,
    ResidentialUnitCustomFieldDto,
    request,
    LandingPageMessageFileUploadDto,
    BygrModule,
    LandingPageMessageData,
} from '@ygtechnologies/bygr-frontend-component-library';
import { LandingPageImageData, ProjectMilestone as LandingPageMilestone, OptionalId } from '@/types';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';

export async function addLandingPageImage(
    projectId: number,
    imageFile: FileData,
    sortOrder: number,
    constructionPhaseId: number | null
): Promise<LandingPageImageData> {
    const x = await request(
        `v1/project/${projectId}/landing-page-images`,
        {
            method: 'POST',
            json: true,
            query: { constructionPhaseId, bygrModule: BygrModule.ChangeManagement },
            body: { imageFile, sortOrder },
        },
        getCoreApiUrl()
    );
    return await x.json();
}
export async function getLandingPageImages(
    projectId: number,
    constructionPhaseId: number | null
): Promise<LandingPageImageData[]> {
    const x = await request(
        `v1/project/${projectId}/landing-page-images`,
        {
            query: { constructionPhaseId },
        },
        getCoreApiUrl()
    );
    return await x.json();
}

export async function deleteLandingPageImage(imageId: number) {
    return request(
        `v1/landing-page-image/${imageId}/`,
        {
            method: 'DELETE',
        },
        getCoreApiUrl()
    );
}
export function getLandingPageImage(imageId: number) {
    return getCoreApiUrl() + `/v1/landing-page-image/${imageId}/`;
}
export function getLandingPageImageThumbnail(imageId: number) {
    return getCoreApiUrl() + `/v1/landing-page-image/${imageId}/thumbnail`;
}
export function getLandingPageMessageImage(imageId: number) {
    return getCoreApiUrl() + `/v1/landing-page-message-file/${imageId}/`;
}
export function getLandingPageMessageImageThumbnail(imageId: number) {
    return getCoreApiUrl() + `/v1/landing-page-message-file/${imageId}/thumbnail`;
}

export async function getLandingPageMessages(
    projectId: number,
    constructionPhaseId: number | null
): Promise<LandingPageMessageData[]> {
    const x = await request(
        `v1/project/${projectId}/landing-page-messages`,
        { query: { constructionPhaseId, bygrModule: BygrModule.ChangeManagement } },
        getCoreApiUrl()
    );
    return await x.json();
}
export async function addLandingPageMessage(
    projectId: number,
    header: string,
    message: string,
    files: LandingPageMessageFileUploadDto[],
    shouldSendEmail: boolean,
    shouldSendSms: boolean,
    profileId: number,
    constructionPhaseId: number | null
) {
    return await request(
        `v1/project/${projectId}/landing-page-messages`,
        {
            method: 'POST',
            json: true,
            body: { header, message, shouldSendEmail, profileId, shouldSendSms, files },
            query: { constructionPhaseId },
        },
        getCoreApiUrl()
    );
}

export async function editLandingPageMessage(
    projectId: number,
    guid: string,
    header: string,
    message: string,
    files: LandingPageMessageFileUploadDto[],
    shouldSendEmail: boolean,
    shouldSendSms: boolean,
    constructionPhaseId: number | null
) {
    await request(
        `v1/project/${projectId}/landing-page-messages/${guid}`,
        {
            method: 'PUT',
            json: true,
            body: { header, message, shouldSendEmail, shouldSendSms, files },
            query: { constructionPhaseId },
        },
        getCoreApiUrl()
    );
}

export async function deleteLandingPageMessage(projectId: number, guid: string) {
    await request(
        `v1/project/${projectId}/landing-page-messages/${guid}`,
        {
            method: 'DELETE',
        },
        getCoreApiUrl()
    );
}

export async function setLandingPageMessagePinned(projectId: number, guid: string, isPinned: boolean) {
    await request(
        `v1/project/${projectId}/landing-page-messages/${guid}/pin`,
        {
            method: 'PUT',
            json: true,
            body: isPinned,
        },
        getCoreApiUrl()
    );
}

export function editLandingPageImageText(imageId: number, text: string): Promise<LandingPageImageData> {
    return request(
        `v1/landing-page-image/${imageId}/text`,
        {
            method: 'PUT',
            json: true,
            body: { text },
        },
        getCoreApiUrl()
    ).then((r) => r.json());
}

export function editLandingPageImageOrder(projectId: number, imageIds: number[], constructionPhaseId: number | null) {
    return request(
        `v1/project/${projectId}/landing-page-images/order`,
        {
            method: 'PUT',
            json: true,
            body: { imageIds },
            query: { constructionPhaseId },
        },
        getCoreApiUrl()
    );
}

export function saveLandingPageMarkdown(projectId: number, markdown: string, constructionPhaseId: number | null) {
    return request(
        `v1/project/${projectId}/landing-page-markdown`,
        {
            method: 'PATCH',
            json: true,
            body: { markdown },
            query: { constructionPhaseId },
        },
        getCoreApiUrl()
    );
}

export async function getLandingPageMilestones(
    projectId: number,
    constructionPhaseId: number | null
): Promise<LandingPageMilestone[]> {
    const x = await request(
        `v1/project/${projectId}/milestones`,
        {
            query: { constructionPhaseId },
        },
        getCoreApiUrl()
    );
    return await x.json();
}

export async function setLandingPageMilestones(
    projectId: number,
    milestones: OptionalId<LandingPageMilestone>[],
    constructionPhaseId: number | null
): Promise<LandingPageMilestone[]> {
    const x = await request(
        `v1/project/${projectId}/milestones`,
        {
            method: 'PUT',
            json: true,
            body: { milestones },
            query: { constructionPhaseId },
        },
        getCoreApiUrl()
    );
    return await x.json();
}

//// Details
export interface LandingPageDetailDTO {
    id: number | null;
    residentialUnitCustomFieldTypeId: number;
}
export async function getLandingPageDetails(
    projectId: number,
    constructionPhaseId: number | null
): Promise<LandingPageDetailDTO[]> {
    const x = await request(
        `v1/project/${projectId}/landing-page-details`,
        {
            query: { constructionPhaseId, BygrModule: BygrModule.ChangeManagement },
        },
        getCoreApiUrl()
    );
    return await x.json();
}

export async function setLandingPageDetails(
    projectId: number,
    details: LandingPageDetailDTO[],
    constructionPhaseId: number | null
) {
    await request(
        `v1/project/${projectId}/landing-page-details`,
        {
            method: 'PUT',
            json: true,
            body: { landingPageDetails: details },
            query: { constructionPhaseId, BygrModule: BygrModule.ChangeManagement },
        },
        getCoreApiUrl()
    );
}

export async function getUnitLandingPageDetails(unitId: number): Promise<ResidentialUnitCustomFieldDto[]> {
    const x = await request(`v1/landing-page-details/unit/${unitId}`, { method: 'GET' }, getCoreApiUrl());
    return await x.json();
}

export async function getLandingPageContacts(
    projectId: number,
    constructionPhaseId: number | null
): Promise<ILandingPageContact[]> {
    return await request(
        `v1/project/${projectId}/landing-page-contacts/`,
        {
            method: 'GET',
            query: { constructionPhaseId, bygrModule: BygrModule.ChangeManagement },
        },
        getCoreApiUrl()
    ).then((x) => x.json());
}

export async function upsertLandingPageContact(
    upsertContact: IUpsertLandingPageContactDto,
    projectId: number,
    constructionPhaseId: number | null
): Promise<ILandingPageContact> {
    const x = await request(`${getCoreApiUrl()}/v1/landing-page-contacts/`, {
        method: 'POST',
        json: true,
        body: { ...upsertContact, projectId, constructionPhaseId, bygrModule: BygrModule.ChangeManagement },
    });

    return await x.json();
}

export function getLandingPageContactImage(contactId: number | undefined) {
    if (!contactId) return '';
    return `${getCoreApiUrl()}/v1/landing-page-contacts/${contactId}/image`;
}

export async function deleteLandingPageContact(contactId: number): Promise<void> {
    await request(`${getCoreApiUrl()}/v1/landing-page-contacts/${contactId}`, {
        method: 'DELETE',
    });
}
